import { useState, useEffect, useCallback, useMemo } from "react";
import { SimpleGrid, FormLabel } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Loading from "../../../Component/Loading/loading";
import apiService from "../../../Method/Report/Report_Service";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../../../assets/Table/TableTheme";
import TableWidget from "../../../Component/Table/TableWidget";
import { useDataContext } from "./../../../DataProvider";

const buildColumns = (t) => [
  {
    name: "CATEGORY_NAME",
    label: t("CATEGORY_NAME"),
    options: { filter: false, sort: false, search: true },
  },
  {
    name: "PERCENTAGE",
    label: t("PERCENTAGE"),
    options: {
      filter: false,
      sort: false,
      search: false, 
      customBodyRender: (value) => (value ? `${value}%` : "0%"),
    },
  },
  {
    name: "TOTAL_SALES",
    label: t("TOTAL_SALES"),
    options: {
      filter: false,
      sort: false,
      search: true,
      customBodyRender: (value) => (value ? value.toLocaleString() : "0"),
    },
  },
];

function App() {
  const { t } = useTranslation();
  const [apiData, setApiData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { trigger } = useDataContext();
  const [searchText, setSearchText] = useState("");

  const columns = useMemo(() => buildColumns(t), [t]);
  const [searchColumns, setSearchColumns] = useState(() =>
    columns.filter((c) => c.options?.search !== false).map((c) => c.name)
  );
  useEffect(() => {
    setSearchColumns(columns.filter((c) => c.options?.search !== false).map((c) => c.name));
  }, [columns]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { success, data, error } = await apiService.search(
      "GET_CATEGORY_SALES_REPORT",
      page + 1,
      rowsPerPage
    );

    if (success && data) {
      setApiData(data);
      setTotalCount(data.TOTAL_COUNT || 0);
      setError("");
    } else {
      setError(error?.response?.data?.message || "Error");
    }
    setLoading(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [trigger, fetchData]);

  const filteredData = (apiData?.CATEGORY_DETAILS || []).filter((row) => {
    const q = (searchText || "").toLowerCase().trim();
    if (!q) return true;
    return searchColumns.some((name) =>
      row?.[name]?.toString().toLowerCase().includes(q)
    );
  });

  return (
    <ThemeProvider theme={theme}>
      {error && <p className="error-message">{error}</p>}
      {isLoading && <Loading />}

      {!isLoading && (
        <div style={{ width: "100%", overflowX: "auto", marginTop: "10px" }}>
          <SimpleGrid className="Total_Header" columns={{ base: 1 }}>
            <div>
              <FormLabel className="Total_Header_Left  Total_Header_Right">
                {t("TOTAL_SALES")}
              </FormLabel>
              <FormLabel className="Total_Header_Value">
                {apiData.TOTAL_SALES_ALL
                  ? apiData.TOTAL_SALES_ALL.toLocaleString()
                  : "0"}
              </FormLabel>
            </div>
          </SimpleGrid>

          <TableWidget
            title={t("CATEGORY_SALES_REPORT")}
            data={filteredData}
            onSearchChange={setSearchText}
            columns={columns}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            searchColumns={searchColumns}
            setSearchColumns={setSearchColumns}
          />
        </div>
      )}
    </ThemeProvider>
  );
}

export default App;
